import React, { useState } from "react";

const LoanCalculator = () => {
    const [loanAmount, setLoanAmount] = useState(0);
    const [downPayment, setDownPayment] = useState(0);
    const [rate, setRate] = useState(0);
    const [tenure, setTenure] = useState(0);
    const [startDate, setStartDate] = useState("");
    const [loanType, setLoanType] = useState("Flat"); // Default to Flat Loan
    const [emiSchedule, setEmiSchedule] = useState([]);

    const calculateEMI = () => {
        const principal = loanAmount - downPayment;
        let schedule = [];
        let date = new Date(startDate);

        if (loanType === "Flat") {
            const interest = (principal * rate * tenure) / (12 * 100);
            const totalAmount = principal + interest;
            const emi = totalAmount / tenure;

            for (let i = 0; i < tenure; i++) {
                schedule.push({
                    month: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
                    emi: emi.toFixed(2),
                    principal: (principal / tenure).toFixed(2),
                    interest: (interest / tenure).toFixed(2),
                    balance: (principal - (i + 1) * (principal / tenure)).toFixed(2),
                });
                date.setMonth(date.getMonth() + 1);
            }
        } else {
            // Reducing Balance Loan Calculation
            const monthlyRate = rate / (12 * 100);
            const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / (Math.pow(1 + monthlyRate, tenure) - 1);

            let remainingBalance = principal;
            for (let i = 0; i < tenure; i++) {
                let interestPaid = remainingBalance * monthlyRate;
                let principalPaid = emi - interestPaid;

                schedule.push({
                    month: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
                    emi: emi.toFixed(2),
                    principal: principalPaid.toFixed(2),
                    interest: interestPaid.toFixed(2),
                    balance: (remainingBalance - principalPaid).toFixed(2),
                });

                remainingBalance -= principalPaid;
                date.setMonth(date.getMonth() + 1);
            }
        }

        setEmiSchedule(schedule);
    };

    return (
        <div className="max-w-screen-xl mx-auto p-6">
            <div className="p-6 shadow-lg rounded-2xl bg-white">
                <h2 className="text-xl font-bold mb-4">Loan EMI Calculator</h2>
                <div className="grid grid-cols-2 gap-4">
                    <input type="number" placeholder="Total Loan Amount" className="p-2 border rounded-lg" onChange={(e) => setLoanAmount(Number(e.target.value))} />
                    <input type="number" placeholder="Down Payment" className="p-2 border rounded-lg" onChange={(e) => setDownPayment(Number(e.target.value))} />
                    <input type="number" placeholder="Rate of Interest (%)" className="p-2 border rounded-lg" onChange={(e) => setRate(Number(e.target.value))} />
                    <input type="number" placeholder="Tenure (Months)" className="p-2 border rounded-lg" onChange={(e) => setTenure(Number(e.target.value))} />
                    <input type="date" className="p-2 border rounded-lg" onChange={(e) => setStartDate(e.target.value)} />
                    <select className="p-2 border rounded-lg" onChange={(e) => setLoanType(e.target.value)}>
                        <option value="Flat">Flat</option>
                        <option value="Reducing">Reducing</option>
                    </select>
                </div>
                <button className="mt-4 w-full bg-blue-500 text-white p-2 rounded-lg" onClick={calculateEMI}>Calculate</button>
            </div>

            {emiSchedule.length > 0 && (
                <div className="mt-6 p-6 shadow-lg rounded-2xl bg-white">
                    <h3 className="text-lg font-semibold mb-4">EMI Schedule ({loanType} Loan)</h3>
                    <table className="w-full border-collapse border border-gray-300">
                        <thead>
                        <tr className="bg-gray-100">
                            <th className="border p-2">Month</th>
                            <th className="border p-2">EMI</th>
                            <th className="border p-2">Principal</th>
                            <th className="border p-2">Interest</th>
                            <th className="border p-2">Balance Left</th>
                        </tr>
                        </thead>
                        <tbody>
                        {emiSchedule.map((emi, index) => (
                            <tr key={index} className="text-center border">
                                <td className="border p-2">{emi.month}</td>
                                <td className="border p-2">&#x20B9; {emi.emi}</td>
                                <td className="border p-2">&#x20B9; {emi.principal}</td>
                                <td className="border p-2">&#x20B9; {emi.interest}</td>
                                <td className="border p-2">&#x20B9; {emi.balance}</td>
                            </tr>
                        ))}
                        <tr className="font-bold bg-gray-200">
                            <td className="border p-2">Total</td>
                            <td className="border p-2">&#x20B9; {(emiSchedule.reduce((sum, emi) => sum + parseFloat(emi.emi), 0)).toFixed(2)}</td>
                            <td className="border p-2">&#x20B9; {(emiSchedule.reduce((sum, emi) => sum + parseFloat(emi.principal), 0)).toFixed(2)}</td>
                            <td className="border p-2">&#x20B9; {(emiSchedule.reduce((sum, emi) => sum + parseFloat(emi.interest), 0)).toFixed(2)}</td>
                            <td className="border p-2">--</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LoanCalculator;
